import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  private messaging: any;
  private messageSource = new BehaviorSubject<any>(null);
  currentMessage = this.messageSource.asObservable();

  constructor(
    @Inject(PLATFORM_ID)
    private platformId: Object,
    private toastr: ToastrService,
  ) {
    this.initMessaging();
  }

  async initMessaging() {
    const supported = await isSupported();

    if (!supported) {
      console.warn('Firebase Messaging is not supported in this browser.');
      return;
    }

    const firebaseApp = initializeApp(environment.firebase);
    this.messaging = getMessaging(firebaseApp);

    this.listenForMessages();
  }

  async requestPermissionAndGetToken(): Promise<any> {
    try {
      if (!(typeof window !== 'undefined' && typeof navigator !== 'undefined'))
        return;

      const supported = await isSupported();
      if (!supported) return;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission not granted.');
        return;
      }

      const tokenPromise = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey,
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject('FCM timeout'), 6000),
      );

      const token = await Promise.race([tokenPromise, timeoutPromise]);

      if (token) {
        localStorage.setItem('fcmToken', token as string);
        console.log('FCM Token:', token);
        return token;
      }
    } catch (error) {
      console.error('Token error:', error);
    }
  }

  listenForMessages() {
    if (isPlatformBrowser(this.platformId) && this.messaging) {
      onMessage(
        this.messaging,
        (payload: {
          notification?: {
            title?: string;
            body?: string;
            icon?: string;
          };
        }) => {
          console.log('Message received in foreground:', payload.notification);

          // if (payload.notification?.title && payload.notification?.body) {
          //   this.showBrowserNotification(payload.notification);
          // }

          // this.messageSource.next(payload.notification);
        },
      );
    }
  }

  showBrowserNotification(notification: {
    title?: string;
    body?: string;
    icon?: string;
  }) {
    const title = notification.title ?? 'No title';
    const body = notification.body ?? 'No message';
    const icon = notification.icon ?? '/assets/default-icon.png';

    if (document.visibilityState === 'visible') {
      // User is on the app tab
      this.toastr.success(body, title);
    } else {
      // User is on another tab or window
      if (Notification.permission === 'granted') {
        new Notification(title, { body, icon });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            new Notification(title, { body, icon });
          }
        });
      }
    }
  }
}
